
import {createContext, useEffect, useState} from "react";
import axios from "axios";
import Cookies from "js-cookie";

export interface StripeAccountStatus {
    success: boolean;
    message: string;
    hasStripeAccount: boolean;
    detailsSubmitted: boolean;
    hasPendingRequirements: boolean;
    transfersActive: boolean;
    isComplete: boolean;
    onboardingUrl?: string;
}

export const UserContext = createContext<UserContextType>({
    ready: false,
    user: null,
    setUser: () => { },
    setProfileFetched: () => { },
    stripeStatus: null,
    stripeStatusLoading: false,
    refreshStripeStatus: async () => { }
});
interface User {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    profilePicture: string;
    bio: string;
    website: string;
    token: string;
    isHost: boolean;
}

interface UserContextType {
    ready: boolean;
    user: User | null;
    setUser: (user: User | null) => void;
    setProfileFetched: (profileFetched: boolean) => void;
    stripeStatus: StripeAccountStatus | null;
    stripeStatusLoading: boolean;
    refreshStripeStatus: () => Promise<void>;
}


export function UserContextProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [ready, setReady] = useState(false);
    const [profileFetched, setProfileFetched] = useState(false); // New state to track profile fetching
    const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
    const [stripeStatusLoading, setStripeStatusLoading] = useState(false);

    const fetchStripeStatus = async () => {
        const token = Cookies.get("authToken");
        if (!token) {
            console.log("No auth token found for Stripe status fetch");
            return;
        }

        console.log("Fetching Stripe account status...");
        setStripeStatusLoading(true);
        try {
            const { data } = await axios.get('/api/payment/stripe-account-status', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            console.log("Stripe status response:", data);
            setStripeStatus(data);
        } catch (error) {
            console.error("Error fetching Stripe status:", error);
            // Set a default incomplete status on error so notification still shows
            setStripeStatus({
                success: false,
                message: "Failed to fetch Stripe status",
                hasStripeAccount: false,
                detailsSubmitted: false,
                hasPendingRequirements: true,
                transfersActive: false,
                isComplete: false
            });
        } finally {
            setStripeStatusLoading(false);
        }
    };

    const refreshStripeStatus = async () => {
        await fetchStripeStatus();
    };

    useEffect(() => {
        const token = Cookies.get("authToken"); // Retrieve the token from cookies
        if (!token) return;
        if (profileFetched && user) return;
        if (!token && profileFetched) {
            setProfileFetched(false);
        }
        axios.get('/api/profile/getProfile', {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then(({ data }) => {
            setUser(data);
            setReady(true);
            setProfileFetched(true);
        }).catch((error) => {
            console.error("Error fetching profile:", error);
            console.error("Error response data:", error.response?.data);
            console.error("Error response status:", error.response?.status);
            // If profile fetch fails, remove invalid token and mark as ready
            Cookies.remove("authToken");
            setUser(null);
            setReady(true);
            setProfileFetched(false);
        });

    }, [user, profileFetched]);

    // Fetch Stripe status when user is a host
    useEffect(() => {
        console.log("Stripe status useEffect triggered:", {
            user: user?.name,
            isHost: user?.isHost,
            stripeStatus,
            stripeStatusLoading
        });
        if (user && user.isHost && !stripeStatus && !stripeStatusLoading) {
            console.log("Conditions met - fetching Stripe status");
            fetchStripeStatus();
        }
        // Reset stripe status when user logs out
        if (!user) {
            setStripeStatus(null);
        }
    }, [user]);

  return (
      <UserContext.Provider value={{ user, setUser, ready, setProfileFetched, stripeStatus, stripeStatusLoading, refreshStripeStatus }}>
      {children}
    </UserContext.Provider>
  );
}